import { getNonPublicIfAvailable } from '@trello/business-logic/member';
import type { CardLabelType, LabelColor } from '@trello/labels';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_PII_STRING,
} from '@trello/privacy';

import type {
  CardBadges,
  CardMember,
  CardSticker,
  CoverColor,
  CoverPhoto,
} from 'app/src/components/CanonicalComponents';
import type { CardTemplatesQuery } from './CardTemplatesQuery.generated';

type Board = NonNullable<CardTemplatesQuery>['board'];
type Members = NonNullable<Board>['members'];
type Cards = NonNullable<Board>['cards'];
type Card = Cards[number];

export function normalizeCover(card: Card): CoverPhoto | null {
  const cover = card.cover;

  if (!cover) {
    return null;
  }

  if (cover.color) {
    return {
      url: '',
      color: cover.color as CoverColor,
      size: cover.size as 'normal' | 'full',
    };
  }

  if (!cover.scaled || !cover.edgeColor) {
    return null;
  }

  return {
    url: '',
    edgeColor: cover.edgeColor,
    previews: cover.scaled.map((preview) => {
      const { scaled, height, url, width } = preview;

      return {
        scaled,
        height,
        url,
        width,
      };
    }),
    size: cover.size as 'normal' | 'full',
  };
}

export function normalizeBadges(card: Card): CardBadges {
  const {
    attachments,
    attachmentsByType,
    description,
    checkItems,
  } = card.badges;
  return {
    attachments,
    description,
    attachmentsByType: {
      trello: {
        board: attachmentsByType.trello.board,
        card: attachmentsByType.trello.card,
      },
    },
    checkItems,
    checkItemsChecked: 0,
    comments: 0,
    due: null,
    start: null,
    subscribed: false,
    viewingMemberVoted: false,
    votes: 0,
    dueComplete: false,
    checkItemsEarliestDue: null,
  };
}

export function normalizeLabels(card: Card): CardLabelType[] {
  const { labels } = card;

  return labels.map((label) => {
    const { id, name, color, idBoard } = label;
    return { id, name, idBoard, color: color ? (color as LabelColor) : null };
  });
}

export function normalizeMembers(card: Card, members: Members): CardMember[] {
  const { idMembers } = card;

  const memberModels: CardMember[] = [];

  idMembers.forEach((idMember) => {
    const member = members.find((m) => m.id === idMember);

    if (!member) {
      return;
    }

    const dangerousAvatarUrl = dangerouslyConvertPrivacyString(
      getNonPublicIfAvailable(member, 'avatarUrl'),
    );

    memberModels.push({
      avatars: dangerousAvatarUrl
        ? {
            30: `${dangerousAvatarUrl}/30.png`,
          }
        : null,
      initials: getNonPublicIfAvailable(member, 'initials') || EMPTY_PII_STRING,
      id: member.id,
    });
  });

  return memberModels;
}

export function normalizeStickers(card: Card): CardSticker[] {
  return card.stickers.map((sticker) => {
    const { imageScaled } = sticker;

    const x2 = imageScaled.find((image) => image.url.includes('@2x'));

    return {
      id: sticker.id,
      image: sticker.image,
      imageUrl: sticker.imageUrl,
      left: sticker.left,
      top: sticker.top,
      rotate: sticker.rotate,
      zIndex: sticker.zIndex,
      imageScaled: x2 ? x2.url : null,
    };
  });
}

export function normalizeDataForCanonicalCard(
  card: Card,
  members: Members,
): {
  cover: CoverPhoto | null;
  badges: CardBadges;
  labels: CardLabelType[];
  members: CardMember[];
  stickers: CardSticker[];
} {
  return {
    cover: normalizeCover(card),
    badges: normalizeBadges(card),
    labels: normalizeLabels(card),
    members: normalizeMembers(card, members),
    stickers: normalizeStickers(card),
  };
}
